import { UPLOAD_FIELD, localDateOf } from '@star-sea/shared';

const DISPLAY_EDGE = 2048;

export function isHeic(file: File): boolean {
  return /\.(heic|heif)$/i.test(file.name) || /^image\/hei[cf]/i.test(file.type);
}

/**
 * HEIC 原图原样上传，另附一份 JPEG 显示版：服务端（sharp 预编译版）解不了 HEIC。
 * Safari 能解 HEIC；Chrome 解不了时就不附，服务端先只存原图。
 */
async function displayCopy(file: File): Promise<Blob | null> {
  try {
    const bmp = await createImageBitmap(file);
    const s = Math.min(1, DISPLAY_EDGE / Math.max(bmp.width, bmp.height));
    const w = Math.max(1, Math.round(bmp.width * s)), h = Math.max(1, Math.round(bmp.height * s));
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bmp, 0, 0, w, h);
    bmp.close();
    return await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.88 });
  } catch {
    return null;
  }
}

export async function uploadForm(file: File): Promise<FormData> {
  const form = new FormData();
  form.set(UPLOAD_FIELD.original, file, file.name);
  // 读不到 EXIF 时，服务端用文件的修改时间（按这台设备的时区）归档
  const t = new Date(file.lastModified || Date.now());
  form.set(UPLOAD_FIELD.fallbackDate, localDateOf(t));
  form.set(UPLOAD_FIELD.fallbackTakenAt, t.toISOString());
  if (isHeic(file)) {
    const display = await displayCopy(file);
    if (display) form.set(UPLOAD_FIELD.display, display, 'display.jpg');
  }
  return form;
}

/** 给设备起个默认名字，方便以后认出来、移除 */
export function deviceLabel(): string {
  const ua = navigator.userAgent;
  const os = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Macintosh/.test(ua)
          ? 'Mac'
          : /Windows/.test(ua)
            ? 'Windows'
            : 'Linux';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : '浏览器';
  return `${os} · ${browser}`;
}
